import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateSurveyStatusMutation } from '@/app/(main)/(home)/_hooks/useUpdateSurveyStatusMutation';
import type { Status } from '@/models/status';
import type { GradedSurveyEdit, SurveyEdit, SurveyHome } from '@/models/survey';
import { checkEndDateBeforeOpenSurvey } from '@/utils/common';
export const useUpdateSurveyStatus = survey => {
  const $ = _c(41);
  const {
    t
  } = useTranslation();
  const updateSurveyStatusMutation = useUpdateSurveyStatusMutation();
  const [updatedStatus, setUpdatedStatus] = useState(null);
  const isSurveyExpired = checkEndDateBeforeOpenSurvey(survey?.expires_at ?? null, survey?.status.label ?? "", updatedStatus?.label ?? "");
  const isSurveyMissingParticipantBlock = updatedStatus?.label === "open" && (survey?.sessions?.length ?? 0) > 0 && survey?.has_participant_block === false;
  const isStartingGradedSurvey = updatedStatus?.label === "open" && survey?.type?.slug === "graded";
  let t0;
  bb0: {
    if (!updatedStatus) {
      t0 = null;
      break bb0;
    }
    switch (true) {
      case isSurveyMissingParticipantBlock:
        {
          let t1;
          if ($[0] !== t) {
            t1 = t("noParticipant.title");
            $[0] = t;
            $[1] = t1;
          } else {
            t1 = $[1];
          }
          let t2;
          if ($[2] !== t) {
            t2 = t("noParticipant.subtitle");
            $[2] = t;
            $[3] = t2;
          } else {
            t2 = $[3];
          }
          let t3;
          if ($[4] !== t1 || $[5] !== t2) {
            t3 = {
              title: t1,
              subTitle: t2
            };
            $[4] = t1;
            $[5] = t2;
            $[6] = t3;
          } else {
            t3 = $[6];
          }
          t0 = t3;
          break bb0;
        }
      case isSurveyExpired:
        {
          let t1;
          if ($[7] !== t) {
            t1 = t("expireWarning.title");
            $[7] = t;
            $[8] = t1;
          } else {
            t1 = $[8];
          }
          let t2;
          if ($[9] !== t) {
            t2 = t(`expireWarning.subtitle`);
            $[9] = t;
            $[10] = t2;
          } else {
            t2 = $[10];
          }
          let t3;
          if ($[11] !== t1 || $[12] !== t2) {
            t3 = {
              title: t1,
              subTitle: t2
            };
            $[11] = t1;
            $[12] = t2;
            $[13] = t3;
          } else {
            t3 = $[13];
          }
          t0 = t3;
          break bb0;
        }
      case isStartingGradedSurvey:
        {
          let t1;
          if ($[14] !== t) {
            t1 = t("survey.graded.warning.title");
            $[14] = t;
            $[15] = t1;
          } else {
            t1 = $[15];
          }
          let t2;
          if ($[16] !== t) {
            t2 = t("survey.graded.warning.subtitle");
            $[16] = t;
            $[17] = t2;
          } else {
            t2 = $[17];
          }
          let t3;
          if ($[18] !== t1 || $[19] !== t2) {
            t3 = {
              title: t1,
              subTitle: t2
            };
            $[18] = t1;
            $[19] = t2;
            $[20] = t3;
          } else {
            t3 = $[20];
          }
          t0 = t3;
          break bb0;
        }
      default:
        {
          t0 = null;
        }
    }
  }
  const confirmationModalContent = t0;
  let t1;
  if ($[21] !== survey?.status?.id || $[22] !== updateSurveyStatusMutation) {
    t1 = (statusId, surveyId) => {
      if (!surveyId) {
        return;
      }
      if (survey?.status?.id && statusId === survey?.status?.id) {
        setUpdatedStatus(null);
        return;
      }
      updateSurveyStatusMutation.mutate({
        surveyId,
        statusId
      }, {
        onSettled: () => {
          setUpdatedStatus(null);
        }
      });
    };
    $[21] = survey?.status?.id;
    $[22] = updateSurveyStatusMutation;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  survey?.status?.id;
  const updateSurveyStatus = t1;
  let t2;
  if ($[24] !== confirmationModalContent || $[25] !== survey?.id || $[26] !== updateSurveyStatus || $[27] !== updateSurveyStatusMutation.isPending || $[28] !== updatedStatus) {
    t2 = () => {
      if (!updatedStatus || confirmationModalContent || updateSurveyStatusMutation.isPending) {
        return;
      }
      updateSurveyStatus(updatedStatus.id, survey?.id);
    };
    $[24] = confirmationModalContent;
    $[25] = survey?.id;
    $[26] = updateSurveyStatus;
    $[27] = updateSurveyStatusMutation.isPending;
    $[28] = updatedStatus;
    $[29] = t2;
  } else {
    t2 = $[29];
  }
  const t3 = survey?.id;
  let t4;
  if ($[30] !== confirmationModalContent || $[31] !== t3 || $[32] !== updateSurveyStatus || $[33] !== updateSurveyStatusMutation.isPending || $[34] !== updatedStatus) {
    t4 = [updatedStatus, confirmationModalContent, updateSurveyStatus, t3, updateSurveyStatusMutation.isPending];
    $[30] = confirmationModalContent;
    $[31] = t3;
    $[32] = updateSurveyStatus;
    $[33] = updateSurveyStatusMutation.isPending;
    $[34] = updatedStatus;
    $[35] = t4;
  } else {
    t4 = $[35];
  }
  useEffect(t2, t4);
  let t5;
  if ($[36] !== confirmationModalContent || $[37] !== updateSurveyStatus || $[38] !== updateSurveyStatusMutation.isPending || $[39] !== updatedStatus) {
    t5 = {
      confirmationModalContent,
      updatedStatus,
      setUpdatedStatus,
      updateSurveyStatus,
      isLoadingUpdateStatus: updateSurveyStatusMutation.isPending
    };
    $[36] = confirmationModalContent;
    $[37] = updateSurveyStatus;
    $[38] = updateSurveyStatusMutation.isPending;
    $[39] = updatedStatus;
    $[40] = t5;
  } else {
    t5 = $[40];
  }
  return t5;
};